<template>
  <div>
    <breadcrumbs :home="product.name + ': Return to in-bond'"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

            <v-row>
              <v-col cols="6" sm="4" lg="3">
                <date-picker v-model="transaction.date" @change="refresh"/>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="10" class="text-caption mb-3">
                The refund provisions of the Excise Act, 2001 specify that a refund may be paid to you where you have filed
                within two years of returning packaged alcohol to an excise warehouse licensee.
                [section 186 of the Act; section 4 of the Return of Packaged Alcohol to an Excise Warehouse Regulations]
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-select label="Original move from in-bond" :items="product.transactions" item-value="refId" v-model="transaction.moveToDutyPaidId" outlined dense
                          no-data-text="No transactions found" :rules="[fieldRules.required]">
                  <template v-slot:item="{item}">
                    <v-container class="px-1 py-3">
                      <v-row dense>
                        <v-col cols="5">{{item.date | formatDateTime}}</v-col>
                        <v-col cols="4" v-if="company.settings.lotTrackingForProducts">Lot {{item.lot}}</v-col>
                        <v-col cols="3">{{-item.bottles | number(0)}} bottles</v-col>
                      </v-row>
                    </v-container>
                  </template>
                  <template v-slot:selection="{item}">
                    <v-container class="px-1 py-3">
                      <v-row dense>
                        <v-col cols="5">{{item.date | formatDateTime}}</v-col>
                        <v-col cols="4" v-if="company.settings.lotTrackingForProducts">Lot {{item.lot}}</v-col>
                        <v-col cols="3">{{-item.bottles | number(0)}} bottles</v-col>
                      </v-row>
                    </v-container>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <cases-bottles :cases.sync="transaction.cases" :bottles.sync="transaction.bottles" :product="product" :availableCount="available" avaiableSuffix="Available"/>

            <v-row>
              <v-col cols="12" sm="6" lg="4" v-if="fromSites.length>1">
                <v-autocomplete outlined dense label="From site" v-model="transaction.fromSiteId" :items="fromSites" item-value="id" item-text="name"/>
              </v-col>
            </v-row>

            <v-row >
              <v-col>
                <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
              </v-col>
            </v-row>
          </v-container>
      </v-form>
      <v-divider/>
      <v-card-actions class="ml-6 mr-6">
        <template v-if="transactionId && company.permissions.includes('inventory.write')">
          <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format,add} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import CasesBottles from '@/components/CasesBottles'

export default {
  name: 'InventoryProductsMoveToInBond',
  mixins: [confirmLeave],
  components: {CasesBottles},
	props: {
    productId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      init: true,
      edit: false,
      product: {name:'',sites:[],transactions:[]},
      valid:true,
      saving:false,
      transaction: {productId:this.productId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                    cases:0, bottles: 0, moveToDutyPaidId:'', comments: '',fromSiteId:''},
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    fromSites() {
      return this.$store.getters.sites(!this.transactionId);
    },
    moveToDutyPaid() {
      return this.product.transactions.find(t => t.refId === this.transaction.moveToDutyPaidId);
    },
    available() {
      return Math.min(this.product.sites.reduce((bottles, site) => bottles + (site.siteId === this.transaction.fromSiteId &&
                                                                              (!this.company.settings.logTrackingForDutyPaid ||
                                                                              site.lot === this.moveToDutyPaid?.lot) ? site.dutyPaidBottles : 0),
                                                0),
                      this.moveToDutyPaid?.bottles || 0);
    }
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    if (this.transactionId) {
      const results = await this.$apiCall(`query InventoryProductsMoveToInBond($id:ID!) {
                                                productMoveToInBond(id:$id) {
                                                  id,productId,date,bottles,cases,moveToDutyPaidId,comments,fromSiteId
                                                }
                                              }`,
                                          {id:this.transactionId});
      if (results.error) {
        this.$nextTick(() => this.dirty = false);
        this.$router.back();
        return;
      }
      this.transaction = results.data.productMoveToInBond;
    }
    else {
      this.transaction.fromSiteId = this.fromSites[0].id;
      this.edit = true;
    }
    this.init = false;
    await this.refresh();
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    async refresh() {
      const from = format(add(new Date(this.transaction.date), {years:-2, days:1}), 'yyyy-MM-dd');
      const to = format(new Date(this.transaction.date), 'yyyy-MM-dd');
      const filters = [{field:'date',comparator:'ge',value:`${from} 00:00:00`},
                       {field:'date',comparator:'le',value:`${to} 23:59:59`},
                       {field:'inBond',comparator:'eq',value:`true`},
                       {field:'inBond',comparator:'eq',value:`true`},
                       {field:'refType', comparator:'eq', value:"ProductTransactionMoveToDutyPaid"}];
      const results = await this.$apiCall(`query InventoryProductsMoveToInBond($id:ID!,$asof:DateTime!,$filters:[FilterInput]) {
                                                product(id:$id) {
                                                  id,name,caseSize,mixedPack,containerType
                                                  sites(asof:$asof) {siteId,lot,dutyPaidBottles}
                                                  transactions2(first:100, filters:$filters) {
                                                    edges {
                                                      node {
                                                        date,refId,lot,siteId,bottles
                                                      }
                                                    }
                                                  }
                                                }
                                              }`,
                                          {id:this.transaction ? this.transaction.productId : this.productId,
                                           asof:this.transaction.date,
                                           filters});
      if (!results.error) {
        this.product = results.data.product;
        this.product.transactions = this.product.transactions2.edges.map(edge => edge.node);
        if (!this.moveToDutyPaid)
          this.transaction.moveToDutyPaidId = '';
        this.$nextTick(() => {this.$refs.form.validate()});
      }
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (this.valid) {
        this.saving = true;
        const results = await this.$apiCall(`mutation InventoryProductsMoveToInBond($input: ProductMoveToInBondInput!) {
                                                productMoveToInBondCreate(input:$input) {id}
                                              }`,
                                            {input:this.transaction});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} return to in-bond transaction for ${this.product.name}`});
        }
      }
		}
	}
}
</script>